// Weboldalak — közös típusok és segédek a felülethez (a szerződés: Tervező → „Weboldalak — architektúra és API-szerződés").
// A szerver-oldal párhuzamosan készül: minden alakot tűrően olvasunk (tömb vagy JSON-szöveg, ms vagy mp, ISO-dátum).
import { useEffect, useState } from 'react';
import { ApiError, api } from '../../lib/api';

export type SiteKind = 'static' | 'proxy' | 'container';
export interface SiteRoute { path: string; to: string; headers?: Record<string, string>; no_buffer?: boolean; timeout?: string; body_limit?: string }
export interface ContainerMount { host: string; in: string; ro?: boolean }
export interface ContainerPort { host: number | string; in: number | string }
export interface ContainerRecipe {
  image?: string; packages?: string[]; setup?: string[]; cmd?: string; workdir?: string; mounts?: ContainerMount[]; env_file?: string;
  env?: Record<string, string>; network?: 'bridge' | 'host'; ports?: ContainerPort[]; memory?: string; cpus?: number | string; healthcheck?: string; tz?: string;
}
export interface TestState { running?: boolean; port?: number | null; url?: string | null; public_url?: string | null; dns?: { ok?: boolean; note?: string | null } | null; started_at?: number | string | null }
// 2. kör: kiadás-ellenőrzés, mentés-ütemezés, „mentésre élesít”
export interface KeepAssets { path: string; days: number }
export interface WatchCfg { paths: string[]; build: string | null; from: string; debounce_sec: number }
export interface SiteLive { running?: boolean | null; health?: unknown; cert_expires?: number | string | null; last_deploy?: number | string | null; uptime?: number | null; uptime_sec?: number | null; since?: number | string | null; test?: TestState | null; unit_state?: string | null; container_status?: string | null; restarts?: number | null; error?: string | null }
export interface Site {
  id: number; name: string; title?: string | null; domains: string[]; project_id: number | null; kind: SiteKind;
  upstream_port?: number | null; unit?: string | null; static_root?: string | null; routes?: SiteRoute[]; spa?: number | boolean;
  headers?: Record<string, string>; precompressed?: number | boolean; compress?: number | boolean; body_limit?: string | null;
  maintenance?: number | boolean; maintenance_text?: string | null; health_path?: string | null; env_path?: string | null; db_paths?: string[];
  container?: ContainerRecipe | null; advanced?: string | null; notes?: string | null; imported_from?: string | null;
  created_at?: number; updated_at?: number; live?: SiteLive | null; test?: TestState | null;
  health_paths?: string[]; required_headers?: Record<string, string[]>; smoke?: string | null; auto_rollback?: number | boolean | null;
  busy_path?: string | null; backup_schedule?: string | null; backup_keep?: number | null; backup_on_deploy?: number | boolean | null;
  keep_assets?: KeepAssets | null; watch?: WatchCfg | null; test_expose?: number | boolean | null;
}
export interface EdgeCert { domain: string; expires: number | string | null }
export interface EdgeState {
  installed?: boolean; running?: boolean; mode?: 'test' | 'prod' | string; ports?: unknown; email?: string | null; cf_token?: boolean;
  sites?: number; certs?: EdgeCert[]; last_apply?: number | string | { at?: number; ok?: boolean; error?: string | null; warnings?: number; sites?: number } | null;
  version?: string | null; cutover?: { at?: number; ok?: boolean } | null;
}
export interface ProjectRef { id: number; name: string; dir?: string }

export const KIND_LABEL: Record<SiteKind, string> = { static: 'statikus', proxy: 'proxy', container: 'konténer' };

// DB-ből jöhet JSON-szövegként is: tömb / objektum normalizálása.
function parseJson<T>(v: unknown, fallback: T): T {
  if (v == null || v === '') return fallback;
  if (typeof v === 'string') { try { return JSON.parse(v) as T; } catch { return fallback; } }
  return v as T;
}
// Szöveg-lista tűrően: tömb, JSON-szöveg, vagy vesszővel / sortöréssel tagolt szöveg
export function strList(v: unknown): string[] {
  if (v == null || v === '') return [];
  if (typeof v === 'string') { const t = v.trim(); if (t.startsWith('[')) return strList(parseJson<unknown>(t, [])); return t.split(/[,\n]/).map((x) => x.trim()).filter(Boolean); }
  return Array.isArray(v) ? v.map((x) => String(x).trim()).filter(Boolean) : [];
}
const numOr = (v: unknown, d: number | null): number | null => { const n = typeof v === 'string' && v.trim() === '' ? NaN : Number(v); return v != null && Number.isFinite(n) ? n : d; };
function readRequiredHeaders(v: unknown): Record<string, string[]> {
  const o = parseJson<unknown>(v, {});
  if (!o || typeof o !== 'object' || Array.isArray(o)) return {};
  return Object.fromEntries(Object.entries(o as Record<string, unknown>).map(([k, x]) => [k, strList(x)]));
}
function readKeepAssets(v: unknown): KeepAssets | null {
  const o = parseJson<Record<string, unknown> | null>(v, null);
  if (!o || typeof o !== 'object') return null;
  return { path: typeof o.path === 'string' ? o.path : 'assets', days: numOr(o.days, 7) ?? 7 };
}
function readWatch(v: unknown): WatchCfg | null {
  const o = parseJson<Record<string, unknown> | null>(v, null);
  if (!o || typeof o !== 'object') return null;
  return { paths: strList(o.paths), build: typeof o.build === 'string' && o.build ? o.build : null, from: typeof o.from === 'string' ? o.from : '', debounce_sec: numOr(o.debounce_sec, 20) ?? 20 };
}
export function normalizeSite(raw: Record<string, unknown>): Site {
  const s = raw as unknown as Site;
  const domains = parseJson<unknown>(raw.domains, []);
  return {
    ...s,
    domains: Array.isArray(domains) ? domains.map(String) : typeof domains === 'string' ? [domains] : [],
    routes: parseJson<SiteRoute[]>(raw.routes, []),
    headers: parseJson<Record<string, string>>(raw.headers, {}),
    db_paths: parseJson<string[]>(raw.db_paths, []),
    container: parseJson<ContainerRecipe | null>(raw.container, null),
    live: parseJson<SiteLive | null>(raw.live, null),
    health_paths: strList(raw.health_paths),
    required_headers: readRequiredHeaders(raw.required_headers),
    keep_assets: readKeepAssets(raw.keep_assets),
    watch: readWatch(raw.watch),
    backup_keep: numOr(raw.backup_keep, null),
  };
}

// Időpont ms-ben: szám (mp vagy ms) vagy ISO-szöveg.
export function toMs(v: unknown): number | null {
  if (v == null || v === '') return null;
  if (typeof v === 'number') return v < 1e12 ? v * 1000 : v;
  if (typeof v === 'string') { const n = Number(v); if (Number.isFinite(n) && v.trim() !== '') return toMs(n); const t = Date.parse(v); return Number.isFinite(t) ? t : null; }
  return null;
}
export function daysLeft(v: unknown): number | null { const t = toMs(v); return t == null ? null : Math.floor((t - Date.now()) / 86_400_000); }
export function certClass(days: number | null): string { return days == null ? '' : days < 0 ? 'bad' : days <= 14 ? 'warn' : 'ok'; }
export function dur(sec: number): string {
  const d = Math.floor(sec / 86400); const h = Math.floor((sec % 86400) / 3600); const m = Math.floor((sec % 3600) / 60);
  return d ? `${d} n ${h} ó` : h ? `${h} ó ${m} p` : m ? `${m} p` : `${Math.max(0, Math.floor(sec))} mp`;
}
export const flag = (v: unknown) => v === true || v === 1 || v === '1';

// Egészség: true / 'ok' / {ok:true} → rendben; false / 'fail' / {ok:false, error} → hiba; különben ismeretlen.
export function healthOf(live: SiteLive | null | undefined): { state: 'ok' | 'bad' | 'unknown'; text: string } {
  const h = live?.health;
  if (h == null) return { state: 'unknown', text: '' };
  if (h === true || h === 'ok' || h === 'healthy') return { state: 'ok', text: 'health rendben' };
  if (h === false) return { state: 'bad', text: 'health hiba' };
  if (typeof h === 'string') return { state: /ok|200/i.test(h) ? 'ok' : 'bad', text: `health: ${h}` };
  if (typeof h === 'object') {
    const o = h as { ok?: boolean; status?: number | string; http?: number | null; error?: string; ms?: number };
    const code = o.http ?? o.status;
    const ok = o.ok ?? (typeof code === 'number' ? code < 400 : undefined);
    const extra = [code != null ? String(code) : '', o.ms != null ? `${o.ms} ms` : ''].filter(Boolean).join(' · ');
    if (ok === true) return { state: 'ok', text: `health rendben${extra ? ` (${extra})` : ''}` };
    if (ok === false) return { state: 'bad', text: `health hiba${o.error ? `: ${o.error}` : extra ? ` (${extra})` : ''}` };
  }
  return { state: 'unknown', text: '' };
}
export function uptimeOf(live: SiteLive | null | undefined): string {
  if (!live) return '';
  const sec = live.uptime_sec ?? live.uptime;
  if (typeof sec === 'number' && sec > 0) return dur(sec);
  const since = toMs(live.since); return since ? dur((Date.now() - since) / 1000) : '';
}
// A kártya és a fejléc lámpája: karbantartás > hiba > fut > áll.
export function lampOf(s: Site): 'maint' | 'bad' | 'run' | 'stop' | 'unk' {
  if (flag(s.maintenance)) return 'maint';
  const running = s.live?.running;
  if (running === true) return healthOf(s.live).state === 'bad' ? 'bad' : 'run';
  if (running === false) return 'stop';
  return 'unk';
}
export const LAMP_TEXT: Record<ReturnType<typeof lampOf>, string> = { maint: 'karbantartás', bad: 'fut, de hibás', run: 'fut', stop: 'áll', unk: 'ismeretlen állapot' };

// Olvasható hibaüzenet: a szerver-oldal még készülhet (404), vagy hibázhat (500).
export function errText(e: unknown, what = ''): string {
  const pre = what ? `${what}: ` : '';
  if (e instanceof ApiError) {
    if (e.status === 404 && (e.code === 'not_found' || e.code === 'http_404' || e.code === 'Not Found')) return `${pre}nem elérhető (404) — a szerver-oldal még készül, vagy APM Restart kell.`;
    if (e.status === 401) return `${pre}lejárt a belépés, töltsd újra az oldalt.`;
    if (e.status >= 500) return `${pre}szerverhiba (${e.status})${e.detail ? ` — ${e.detail}` : e.code && !e.code.startsWith('http_') ? ` — ${e.code}` : ''}.`;
    return `${pre}${e.detail || e.code}`;
  }
  if (e instanceof SyntaxError) return `${pre}érthetetlen válasz a szervertől (nem JSON).`;
  if (e instanceof TypeError) return `${pre}nincs kapcsolat a szerverrel.`;
  return `${pre}${e instanceof Error ? e.message : 'ismeretlen hiba'}`;
}

// Módosító hívásnál a szerver 200-zal is jelezhet kudarcot: { ok:false, error } → kivétel (olvasható üzenettel)
export function failOf(r: unknown): void {
  if (r && typeof r === 'object' && (r as { ok?: unknown }).ok === false) {
    const o = r as { error?: string; note?: string };
    throw new Error(o.error || o.note || 'nem sikerült');
  }
}
export async function siteApi<T>(id: number | string, path = '', opts: RequestInit = {}): Promise<T> {
  const r = await api<T>(`/api/n4/sites/${id}${path}`, opts);
  if ((opts.method ?? 'GET').toUpperCase() !== 'GET') failOf(r);
  return r;
}
// A szerver emberi nyelvű visszajelzése (note), ha van
export const noteOf = (r: unknown): string => (r && typeof r === 'object' && typeof (r as { note?: unknown }).note === 'string' ? (r as { note: string }).note : '');
export const post = (body?: unknown): RequestInit => ({ method: 'POST', ...(body !== undefined ? { body: JSON.stringify(body) } : {}) });

// Válasz-mezők tűrő kiolvasása: { site } vagy maga a site; { releases } vagy tömb stb.
export function pickSite(r: unknown): Site | null {
  const wrapped = r && typeof r === 'object' && 'site' in (r as object);
  const o = (wrapped ? (r as { site: unknown }).site : r) as Record<string, unknown> | null;
  if (!o || typeof o !== 'object' || !('id' in o)) return null;
  // A részlet-válasz a live-ot külön adja: { site, live }
  const live = wrapped && (r as { live?: unknown }).live && !o.live ? (r as { live: unknown }).live : o.live;
  return normalizeSite({ ...o, live });
}
export function pickList<T>(r: unknown, ...keys: string[]): T[] {
  if (Array.isArray(r)) return r as T[];
  if (r && typeof r === 'object') for (const k of keys) { const v = (r as Record<string, unknown>)[k]; if (Array.isArray(v)) return v as T[]; }
  return [];
}
export function pickText(r: unknown, ...keys: string[]): string {
  if (typeof r === 'string') return r;
  if (r && typeof r === 'object') for (const k of keys) { const v = (r as Record<string, unknown>)[k]; if (typeof v === 'string') return v; if (Array.isArray(v)) return v.map(String).join('\n'); }
  return '';
}

// Projektnevek a projekt-linkhez és a választóhoz.
export function useProjects(): ProjectRef[] {
  const [list, setList] = useState<ProjectRef[]>([]);
  // dir: a projekt munkamappája (work_dir, ha van) — a kiadás „from” útvonala ehhez képest értendő
  useEffect(() => { void api<{ projects: (ProjectRef & { work_dir?: string | null })[] }>('/api/projects').then((r) => setList(r.projects.map((p) => ({ id: p.id, name: p.name, dir: p.work_dir || p.dir })))).catch(() => {}); }, []);
  return list;
}

export function portsText(p: unknown): string {
  if (p == null) return '—';
  if (Array.isArray(p)) return p.join(' / ');
  if (typeof p === 'object') { const o = p as Record<string, unknown>; const h = o.http ?? o.http_port; const s = o.https ?? o.https_port; if (h != null || s != null) return `${h ?? '—'} / ${s ?? '—'}`; return Object.entries(o).map(([k, v]) => `${k} ${String(v)}`).join(' · '); }
  return String(p);
}

// Az edge utolsó alkalmazása: { at, ok, error, warnings } objektum vagy időbélyeg
export function lastApplyOf(v: EdgeState['last_apply']): { at: number | null; ok: boolean | null; error: string; warnings: number } {
  if (v && typeof v === 'object') return { at: toMs(v.at), ok: typeof v.ok === 'boolean' ? v.ok : null, error: v.error ?? '', warnings: typeof v.warnings === 'number' ? v.warnings : 0 };
  return { at: toMs(v), ok: null, error: '', warnings: 0 };
}

// ===== 2. kör: kiadás-ellenőrzés (checks), változás-lista (dry run / kiadás), release-manifest — tűrő olvasók =====
export interface CheckRow { kind: 'health' | 'header' | 'smoke'; path: string; what: string; status: string; ok: boolean | null; detail: string }
const okOf = (o: Record<string, unknown>): boolean | null => {
  if (typeof o.ok === 'boolean') return o.ok;
  const st = Number(o.status ?? o.http ?? o.code);
  return Number.isFinite(st) && st > 0 ? st < 400 : null;
};
const textOf = (v: unknown): string => (v == null ? '' : typeof v === 'string' ? v : typeof v === 'number' || typeof v === 'boolean' ? String(v) : JSON.stringify(v));
// { checks:{health_paths, required_headers, smoke} } vagy maga a checks-objektum → egységes sorok
export function checkRows(v: unknown): CheckRow[] {
  if (!v || typeof v !== 'object') return [];
  const root = v as Record<string, unknown>;
  const c = (root.checks && typeof root.checks === 'object' ? root.checks : root) as Record<string, unknown>;
  const out: CheckRow[] = [];
  const hp = parseJson<unknown>(c.health_paths ?? c.health, []);
  if (Array.isArray(hp)) for (const x of hp) {
    if (typeof x === 'string') { out.push({ kind: 'health', path: x, what: '', status: '', ok: null, detail: '' }); continue; }
    if (!x || typeof x !== 'object') continue;
    const o = x as Record<string, unknown>;
    out.push({ kind: 'health', path: textOf(o.path ?? o.url), what: '', status: textOf(o.status ?? o.http ?? o.code), ok: okOf(o), detail: [o.ms != null ? `${textOf(o.ms)} ms` : '', textOf(o.error ?? o.note)].filter(Boolean).join(' · ') });
  }
  const rh = parseJson<unknown>(c.required_headers ?? c.headers, []);
  const pushHeader = (path: string, o: Record<string, unknown>) => out.push({ kind: 'header', path, what: textOf(o.header ?? o.name), status: '', ok: okOf(o), detail: textOf(o.value ?? o.error ?? o.note) });
  if (Array.isArray(rh)) for (const x of rh) {
    if (!x || typeof x !== 'object') continue;
    const o = x as Record<string, unknown>; const path = textOf(o.path ?? o.url);
    if (o.header != null || o.name != null) { pushHeader(path, o); continue; }
    // csoportosítva: { path, headers:[{name, ok}] } vagy { path, missing:[…], present:[…] }
    if (Array.isArray(o.headers)) for (const hh of o.headers) pushHeader(path, typeof hh === 'string' ? { header: hh, ok: o.ok } : (hh as Record<string, unknown>));
    if (Array.isArray(o.missing)) for (const m of o.missing) out.push({ kind: 'header', path, what: String(m), status: '', ok: false, detail: 'hiányzik' });
    if (Array.isArray(o.present)) for (const m of o.present) out.push({ kind: 'header', path, what: String(m), status: '', ok: true, detail: '' });
    if (!Array.isArray(o.headers) && !Array.isArray(o.missing) && !Array.isArray(o.present)) out.push({ kind: 'header', path, what: '', status: textOf(o.status), ok: okOf(o), detail: textOf(o.error ?? o.note) });
  }
  const sm = parseJson<unknown>(c.smoke, null);
  if (sm && typeof sm === 'object') {
    const o = sm as Record<string, unknown>;
    out.push({ kind: 'smoke', path: textOf(o.url ?? ''), what: textOf(o.name ?? o.scenario ?? 'smoke'), status: '', ok: okOf(o), detail: [textOf(o.error ?? o.note), o.failed != null ? `elbukott lépés: ${textOf(o.failed)}` : '', o.steps != null && typeof o.steps === 'number' ? `${o.steps} lépés` : ''].filter(Boolean).join(' · ') });
  } else if (typeof sm === 'boolean') out.push({ kind: 'smoke', path: '', what: 'smoke', status: '', ok: sm, detail: '' });
  return out;
}

export interface FileChange { path: string; size: number | null; delta?: number | null }
export interface ChangeSet { added: FileChange[]; changed: FileChange[]; removed: FileChange[]; n: { added: number; changed: number; removed: number }; unchanged: number | null; bytes_in: number | null; bytes_out: number | null }
const fileList = (v: unknown): FileChange[] => (Array.isArray(v) ? v.map((x) => (typeof x === 'string' ? { path: x, size: null } : { path: textOf((x as Record<string, unknown>).path), size: numOr((x as Record<string, unknown>).size ?? (x as Record<string, unknown>).bytes, null), delta: numOr((x as Record<string, unknown>).delta, null) })) : []);
const countOf = (v: unknown): number => (Array.isArray(v) ? v.length : numOr(v, 0) ?? 0);
// A dry run és az éles kiadás „changes” mezője: tömbök (dry) vagy összesített számok (éles)
export function changeSetOf(v: unknown): ChangeSet | null {
  const o = parseJson<Record<string, unknown> | null>(v, null);
  if (!o || typeof o !== 'object') return null;
  return {
    added: fileList(o.added), changed: fileList(o.changed), removed: fileList(o.removed),
    n: { added: countOf(o.added), changed: countOf(o.changed), removed: countOf(o.removed) },
    unchanged: numOr(o.unchanged, null), bytes_in: numOr(o.bytes_in, null), bytes_out: numOr(o.bytes_out, null),
  };
}

// Release-manifest: meta {files, bytes, sha1} (objektum vagy JSON-szöveg), és az előzőhöz képesti eltérés, ha a szerver adja
export interface Manifest { files: number | null; bytes: number | null; sha1: string | null }
export function manifestOf(r: Record<string, unknown>): Manifest | null {
  const m = parseJson<Record<string, unknown> | null>(r.meta ?? r.manifest, null);
  const src = m && typeof m === 'object' ? m : r;
  const files = numOr(src.files ?? src.file_count, null); const bytesN = numOr(src.bytes ?? src.size, null);
  if (files == null && bytesN == null) return null;
  return { files, bytes: bytesN, sha1: typeof src.sha1 === 'string' ? src.sha1 : null };
}
export interface ReleaseDiff { added: number | null; changed: number | null; removed: number | null; files: number | null; bytes: number | null }
export function releaseDiffOf(r: Record<string, unknown>): ReleaseDiff | null {
  const d = parseJson<Record<string, unknown> | null>(r.diff ?? r.delta ?? r.vs_prev ?? r.changes, null);
  if (!d || typeof d !== 'object') return null;
  const pick = (k: string) => (d[k] == null ? null : countOf(d[k]));
  return { added: pick('added'), changed: pick('changed'), removed: pick('removed'), files: numOr(d.files, null), bytes: numOr(d.bytes, null) };
}
